import React, { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import ICart from "../../../models/ICart";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetCartDTO from "../../../models/DTO/IOnGetCartDTO";
import CartService from "@services/CartService";
import CartStatusEnum from "../../../models/CartStatusEnum";
import { useCart } from "../../../contexts/CartContext";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import OwTitle from "../../shared/OwTitle/OwTitle";
import styled from "styled-components";
import TitleTagEnum from "../../../models/TitleTagEnum";
import { EcommerceCheckoutShipTo } from "../EcommerceCheckoutShipTo/EcommerceCheckoutShipTo";
import DeliveryDeadlineInfo from "../../shared/DeliveryDeadlineInfo/DeliveryDeadlineInfo";
import { toDeliveryDateKey } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import CustomerDeliveryDatePicker from "../../shared/CustomerDeliveryDatePicker/CustomerDeliveryDatePicker";
import { isDeliveryDateNotSelectableNotifyMessage } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDateRulesUtils";
import IOnUpdateCartShipToDTO from "../../../models/DTO/IOnUpdateCartShipToDTO";
import { EcommerceStripeCardForm } from "../EcommerceStripeCardForm/EcommerceStripeCardForm";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import ICreateCartPaymentIntentDTO from "../../../models/DTO/ICreateCartPaymentIntentDTO";
import IOnCreateCartPaymentIntentDTO from "../../../models/DTO/IOnCreateCartPaymentIntentDTO";
import ICartValidateInventoryDTO from "../../../models/DTO/ICartValidateInventoryDTO";
import ICartOnValidateInventoryDTO from "../../../models/DTO/ICartOnValidateInventoryDTO";
import SocketService from "../../../services/SocketService";
import SupplierService from "../../../services/SupplierService";
import ICart_Item from "../../../models/ICart_Item";
import IUpdateCartItemDTO from "../../../models/DTO/IUpdateCartItemDTO";
import IDeleteCartItemDTO from "../../../models/DTO/IDeleteCartItemDTO";
import IOnDeleteCartItemDTO from "../../../models/DTO/IOnDeleteCartItemDTO";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import OwSkeleton from "../../shared/OwSkeleton/OwSkeleton";
import IUpdatePaymentIntentDTO from "../../../models/DTO/IUpdatePaymentIntentDTO";
import IOnUpdatePaymentIntentDTO from "../../../models/DTO/IOnUpdatePaymentIntentDTO";
import { EcommerceCheckoutCartItem } from "../EcommerceCheckoutCartItem/EcommerceCheckoutCartItem";
import { EcommerceCartKitEditor } from "../EcommerceCartKitEditor/EcommerceCartKitEditor";
import OwButton from "../../shared/OwButton/OwButton";
import ICartCompleteCheckoutDTO from "../../../models/DTO/ICartCompleteCheckoutDTO";
import ICartOnCompleteCheckoutDTO from "../../../models/DTO/ICartOnCompleteCheckoutDTO";
import { useNavigate } from "react-router-dom";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwRadioSelect from "../../shared/OwRadioSelect/OwRadioSelect";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import IOwRadioSelectOption from "../../../models/IOwRadioSelectOption";
import { Socket } from "socket.io-client";
import ICartOnCalculateTotalsDTO from "../../../models/DTO/ICartOnCalculateTotalsDTO";
import ICartCalculateTotalsDTO from "../../../models/DTO/ICartCalculateTotalsDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IUpdateCartPropDTO from "../../../models/DTO/IUpdateCartPropDTO";
import IOnUpdateCartPropDTO from "../../../models/DTO/IOnUpdateCartPropDTO";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { getCapturedUtm } from "../../../utils/UtmCapture";
import EcommerceFulfillmentEnum from "../../../models/EcommerceFulfillmentEnum";
import {
    fulfillmentFlags,
    fulfillmentFromFlags,
    getFulfillmentCorrection,
    getOfferedFulfillmentOptions,
    IResolvedFulfillmentFlags,
} from "../../../utils/ecommerceFulfillment";
import CatalogService from "@services/CatalogService";
import UserService from "@services/UserService";
import PromoCodeService from "@services/PromoCodeService";
import MinimumOrderService from "@services/MinimumOrderService";
import MinOrderTotalNotice from "../../shared/MinOrderTotalNotice/MinOrderTotalNotice";
import { OwPromoCodeInput } from "../../shared/OwPromoCodeInput/OwPromoCodeInput";
import ICartApplyPromoCodeDTO from "../../../models/DTO/ICartApplyPromoCodeDTO";
import ICartRemovePromoCodeDTO from "../../../models/DTO/ICartRemovePromoCodeDTO";
import ICartOnApplyPromoCodeDTO from "../../../models/DTO/ICartOnApplyPromoCodeDTO";

type Props = {};

// Back-off before each retry of a failed cart fetch.
const GET_CART_RETRY_DELAYS_MS = [1000, 3000];

// Sequence for createPaymentIntent requestIds. Module-wide so ids stay unique across visits to checkout on
// one socket: a late answer to an earlier visit's request can never match a request from this one.
let intentRequestSeq = 0;

// Sequence for delivery-day save requestIds (CartUpdateProp), module-wide for the same reason.
let deliveryDateRequestSeq = 0;

// Sequence for fulfillment save requestIds (CartUpdatePaymentIntent / CartUpdateProp), module-wide for the same reason.
let fulfillmentRequestSeq = 0;

export const EcommerceCheckout: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const { refreshInactiveCarts } = useCart();

    const history = useNavigate();

    let currencyName = vendorModel.Catalog?.Currency?.name ? vendorModel.Catalog.Currency.name : "USD";
    if (vendorModel.Catalog?.currency_id) {
        currencyName = CatalogService.getCurrencyNameFromId(vendorModel.Catalog.currency_id);
    }

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    // Card checkout runs through Stripe only when the supplier has Stripe enabled. Net-terms buyers, and
    // buyers of a supplier without Stripe, place the order directly (CartCompleteCheckout, no payment).
    const payWithStripe = !!supplierModel.Supplier_Integration.has_stripe && !vendorModel.net_term_ecomm_user;

    const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyName,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const [cart, setCart] = useState<ICart>();

    const [initTotalsCalculated, setInitTotalsCalculated] = useState(false);
    const [stripeLoaded, setStripeLoaded] = useState(false);
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
    const [paymentIntent, setPaymentIntent] = useState<IOnCreateCartPaymentIntentDTO>();
    const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
    // A delivery-day save (CartUpdateProp) is in flight for this date key: the API releases the attached
    // Stripe intent while handling it, so Place Order is held and the picker is read-only until the server's
    // cart carries this date (any cart-prop response) or this request's own failure Notify arrives, paired
    // by the echoed requestId. Other cart-prop responses and failures leave it pending.
    const pendingDeliveryDateRef = useRef<{ date: string; requestId: string } | null>(null);
    const [deliveryDateSaving, setDeliveryDateSaving] = useState(false);
    const settleDeliveryDateSave = () => {
        pendingDeliveryDateRef.current = null;
        setDeliveryDateSaving(false);
    };
    // "Customer selects date" (OR-1531): the API rejected the chosen date (the cutoff passed while the cart sat
    // open) and named the new earliest date. Shown by the picker until the customer's next pick lands.
    const [rejectedEarliestDate, setRejectedEarliestDate] = useState<string | null>(null);
    const [isCartValid, setIsCartValid] = useState<boolean>(false);
    const [placeOrderActive, setPlaceOrderActive] = useState<boolean>(false);
    const [cartUpdating, setCartUpdating] = useState<boolean>(false);
    const [paymentForm, setPaymentForm] = useState<HTMLFormElement | null>(null);
    const [stripe, setStripe] = useState<Stripe | null>();
    const [elements, setElements] = useState<StripeElements | null>();
    const [setupInventoryOn, setSetupInventoryOn] = useState(false);
    const [updatingItemGuid, setUpdatingItemGuid] = useState("");
    // Kit builder line currently open in the edit modal, if any.
    const [editingKitItem, setEditingKitItem] = useState<ICart_Item>();
    const [promoApplying, setPromoApplying] = useState(false);
    const [promoErrorKey, setPromoErrorKey] = useState<string | null>(null);

    const isAddressValid = useMemo(() => {
        // Local Pickup collects the order at the supplier, so the customer ship-to is not required
        // (the ship-to form is hidden and replaced by the supplier's pickup address).
        if (cart?.is_local_pickup) {
            return true;
        }

        return SupplierService.isAddressValidForCheckout(supplierModel, cart?.Ship_To);
    }, [cart?.Ship_To, cart?.is_local_pickup, supplierModel]);

    // Supplier minimum order total. Cart totals are in the catalog currency, the same currency the minimum is
    // in, so the amounts use the checkout's currencyFormatter. Below the minimum no payment intent is created
    // (the API would reject it) and Place Order stays disabled.
    const minOrder = useMemo(() => MinimumOrderService.evaluateCart(supplierModel, cart), [cart, supplierModel]);
    const isBelowMinOrder = !!minOrder && !minOrder.met;

    // Totals signature of a cart the API rejected for the minimum. While the cart still matches it, the intent
    // effect does not retry, so a client / server disagreement cannot loop create -> reject -> refetch.
    const minOrderRejectedSignatureRef = useRef<string | null>(null);

    // Totals signature of the cart the latest createPaymentIntent request was sent for, until it answers. A
    // minimum-order rejection that carries no requestId (an API that predates the echo, or an update / complete
    // rejection) records this one, not the latest cart: the customer may have raised the total meanwhile.
    // (The socket listeners are bound once on mount, so `notify` reads it through the ref.)
    const intentRequestSignatureRef = useRef<string | null>(null);

    // Pairs createPaymentIntent answers and failures with their requests. Each request sends a fresh requestId,
    // which the API echoes on its answer (and on its Notify if it fails), and records the intent generation it
    // was sent in and the totals signature of its cart. dropPaymentIntent starts a new generation: the cart edit
    // behind it cancelled whatever an earlier request attached, so an answer or error for a request from an
    // older generation must not change the checkout, in whatever order they arrive. The dropping path
    // re-fetches the cart, so the intent effect asks again.
    const intentGenerationRef = useRef(0);
    const intentRequestsRef = useRef(new Map<string, { generation: number; signature: string }>());

    // Id of the intent the card form is on, null once dropped. An updatePaymentIntent answer for any other
    // intent is ignored: it was sent before a cart edit cancelled that intent and must not bring it back.
    const paymentIntentIdRef = useRef<string | null>(null);

    const cartTotalsSignature = (fromCart: ICart | undefined): string => {
        return [fromCart?.sub_total, fromCart?.discount_total, fromCart?.promo_discount_total, fromCart?.Cart_Items?.length].join("|");
    };

    // Fulfillment choice (Ship / Local Pickup / Local Delivery). The cart carries the two order flags
    // (is_local_pickup / is_local_delivery) so the server can stamp them onto the order, including on
    // the Stripe webhook path that has no client payload. Both false = Ship; null = not chosen yet.
    // Which choices are offered (and in what order) comes from the supplier's local flags and
    // settings.screens.eCommerce.fulfillmentOptions, which can hide shipping.
    const offeredFulfillment = useMemo(() => getOfferedFulfillmentOptions(supplierModel), [supplierModel]);

    // The flags ride along on checkout payloads whenever anything but plain shipping is offered.
    const sendsFulfillment = offeredFulfillment.some((option) => option !== EcommerceFulfillmentEnum.SHIPPING);

    // The radio only appears when there is something to choose between.
    const showFulfillmentChoice = offeredFulfillment.length > 1;

    // Shipping instructions are offered only when the supplier turned the checkout field on. NULL on the
    // cart means the customer never touched the field, so it (and the order) falls back to the account's
    // default shipping instructions; an emptied field is sent as "" so the default is cleared for the order.
    const showShippingInstructions = !!supplierModel.ecomm_show_shipping_instructions_on_checkout;

    const resolveShippingInstructions = (cartModel?: ICart): string => {
        const captured = cartModel?.shipping_instructions;

        if (captured !== null && captured !== undefined) {
            return captured;
        }

        return vendorModel.default_shipping_instructions || "";
    };

    // Last value handed to the API (cart prop update or payment intent), so a blur without an edit
    // does not cost a payment intent round trip.
    const sentShippingInstructionsRef = useRef<string | undefined>(undefined);

    const shippingInstructionsPayload = (cartModel?: ICart): { shipping_instructions?: string } => {
        if (!showShippingInstructions) {
            return {};
        }

        const shippingInstructions = resolveShippingInstructions(cartModel);

        sentShippingInstructionsRef.current = shippingInstructions;

        return { shipping_instructions: shippingInstructions };
    };

    const fulfillmentChoice = fulfillmentFromFlags(cart);

    const fulfillmentOptions: IOwRadioSelectOption[] = offeredFulfillment.map((option) => ({ value: option, label: option }));

    // Flags to send with a checkout payload. Only sent when the supplier offers a local option, and
    // always both together so the server can tell an explicit "Ship" from "not mentioned".
    const fulfillmentPayload = (fromCart: ICart | undefined) => {
        if (!sendsFulfillment || !fromCart) {
            return {};
        }

        return fulfillmentFlags(fulfillmentFromFlags(fromCart));
    };

    // Settle the cart's choice the way the server does: a never-chosen cart (both flags null) gets the
    // default (the account's default local option, else the first offered option), and a choice the
    // supplier does not offer (e.g. Ship once shipping is hidden) is replaced by it. The server applies
    // the same rule when it calculates the totals and on every checkout payload.
    const applyFulfillmentDefault = (fromCart: ICart): ICart => {
        // A fulfillment change still waiting for its answer: this cart predates its write, so keep the customer's pick.
        if (fulfillmentSaveRef.current === "saving" && fulfillmentPendingFlagsRef.current) {
            return { ...fromCart, ...fulfillmentPendingFlagsRef.current };
        }

        const correction = getFulfillmentCorrection(fromCart, offeredFulfillment, vendorModel);

        return correction ? { ...fromCart, ...correction } : fromCart;
    };

    // Set when a fulfillment change was pushed through the payment intent so the cart (and its
    // recalculated shipping) is re-fetched once the intent update comes back.
    const refreshCartAfterIntentRef = useRef(false);

    // A fulfillment change being saved. Until the re-fetched cart lands, the summary still shows the previous
    // choice's tax, shipping and total, so they show as skeletons and the radio and Place Order are locked.
    // "saving" runs from the radio change until the save is answered (or fails, or, on the card path, the intent
    // it updated is dropped: every dropping path re-fetches the cart); "refreshing" runs until the next cart from
    // the server is applied. Refs alongside the state because the socket listeners are bound once on mount.
    const fulfillmentSaveRef = useRef<"saving" | "refreshing" | null>(null);
    const [fulfillmentSaving, setFulfillmentSaving] = useState(false);

    // requestId of the pending save (CartUpdatePaymentIntent or CartUpdateProp), echoed on its answer and on its
    // failure Notify.
    const fulfillmentRequestIdRef = useRef<string | null>(null);

    // The choice being saved. A cart that lands while the save is still unanswered predates it, so the checkout
    // keeps showing this choice over it (see applyFulfillmentDefault).
    const fulfillmentPendingFlagsRef = useRef<IResolvedFulfillmentFlags | null>(null);

    const startFulfillmentSave = (requestId: string, flags: IResolvedFulfillmentFlags) => {
        fulfillmentSaveRef.current = "saving";
        fulfillmentRequestIdRef.current = requestId;
        fulfillmentPendingFlagsRef.current = flags;
        setFulfillmentSaving(true);
    };

    const fulfillmentSaveAnswered = () => {
        if (fulfillmentSaveRef.current) {
            fulfillmentSaveRef.current = "refreshing";
            fulfillmentRequestIdRef.current = null;
            fulfillmentPendingFlagsRef.current = null;
        }
    };

    const settleFulfillmentSave = () => {
        fulfillmentSaveRef.current = null;
        fulfillmentRequestIdRef.current = null;
        fulfillmentPendingFlagsRef.current = null;
        setFulfillmentSaving(false);
    };

    /**
     * Whether an update's success answer is the pending fulfillment save's own. The checkout can have other updates
     * in flight on the same channel (shipping instructions or a PO # saved on blur just before the click), and their
     * answers must not settle the save before its write has landed. The API echoes the requestId on these answers
     * (null for an update sent without one); an API that predates the echo sends no requestId key, and then any
     * answer counts, as before.
     */
    const isFulfillmentSaveAnswer = (requestId: string | null | undefined): boolean => {
        return requestId === undefined || (!!fulfillmentRequestIdRef.current && requestId === fulfillmentRequestIdRef.current);
    };

    // Called wherever a cart from the server replaces the local one.
    const settleFulfillmentSaveOnCart = () => {
        if (fulfillmentSaveRef.current === "refreshing") {
            settleFulfillmentSave();
        }
    };

    const refreshOnValidateInventory = () => {
        socket.removeListener(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
        socket.on(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
    };

    const refreshOnCompleteCheckout = () => {
        socket.removeListener(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
        socket.on(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
    };

    const createPaymentIntent = () => {
        if (payWithStripe) {
            const payload: ICreateCartPaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            if (cart?.qbd_ship_via?.ListID) {
                payload.qbd_ship_via = cart.qbd_ship_via;
            }

            Object.assign(payload, fulfillmentPayload(cart), shippingInstructionsPayload(cart));

            const utm = getCapturedUtm();
            if (utm) {
                payload.utm = utm;
            }

            const signature = cartTotalsSignature(cart);

            payload.requestId = `intent-${++intentRequestSeq}`;
            intentRequestsRef.current.set(payload.requestId, { generation: intentGenerationRef.current, signature });

            intentRequestSignatureRef.current = signature;
            socket.emit(EndpointsEnum.CartCreatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const onCreatePaymentIntent = (data: IOnCreateCartPaymentIntentDTO) => {
        // An API that predates the requestId echo sends none; its answers are taken as they come.
        if (data.requestId !== undefined) {
            const request = intentRequestsRef.current.get(data.requestId);
            intentRequestsRef.current.delete(data.requestId);

            // Sent before the latest cart edit (or by an earlier visit to this page): the edit cancelled
            // whatever this request attached.
            if (request?.generation !== intentGenerationRef.current) {
                return;
            }
        }

        intentRequestSignatureRef.current = null;
        setPaymentIntentLoading(false);

        paymentIntentIdRef.current = data.paymentIntentId;
        setPaymentIntent(data);
    };

    // Every cart edit (ship-to, item qty / options / delete, PO # / note, promo code) makes the API cancel
    // the cart's attached Stripe intent and detach it, since its amount may be stale. Updating the cached
    // intent afterwards is refused by Stripe (it is canceled), so drop it instead: the intent effect mints a
    // fresh one once the re-fetched cart lands, and the card form re-mounts on the new client secret.
    const dropPaymentIntent = () => {
        intentGenerationRef.current++;
        paymentIntentIdRef.current = null;
        // A refresh owed to an update of the dropped intent is moot: every dropping path re-fetches the cart.
        refreshCartAfterIntentRef.current = false;
        setPaymentIntent(undefined);
        setPaymentIntentLoading(false);

        // A card-path fulfillment save rode on that intent and its answer will be ignored, so the re-fetched
        // cart settles it instead.
        if (payWithStripe) {
            fulfillmentSaveAnswered();
        }
    };

    const onUpdatePaymentIntent = (data: IOnUpdatePaymentIntentDTO) => {
        // The intent was dropped while this update was in flight. Leave the loading and refresh state alone:
        // they may belong to an update of the replacement intent that is still pending.
        if (data.paymentIntentId !== paymentIntentIdRef.current) {
            return;
        }

        setPaymentIntentLoading(false);
        setPaymentIntent(data);

        // Only the fulfillment update's own answer re-fetches the cart (see isFulfillmentSaveAnswer).
        if (refreshCartAfterIntentRef.current && isFulfillmentSaveAnswer(data.requestId)) {
            refreshCartAfterIntentRef.current = false;
            fulfillmentSaveAnswered();
            getCart();
        }
    };

    /**
     * A cart payload that replaces the whole local cart keeps the holiday dates and schedule exceptions
     * already held when the payload omits them, so the delivery-day preview cannot fall back to the plain
     * template between a cart refresh and the next getCart().
     */
    const keepDeliveryExceptions = (next: ICart, current: ICart | null | undefined): ICart => ({
        ...next,
        deliveryBlackoutDates: next.deliveryBlackoutDates ?? current?.deliveryBlackoutDates,
        deliveryScheduleOverrides: next.deliveryScheduleOverrides ?? current?.deliveryScheduleOverrides,
        deliveryDateRules: next.deliveryDateRules ?? current?.deliveryDateRules,
    });

    const onCalculateTotals = (data: ICartOnCalculateTotalsDTO) => {
        setInitTotalsCalculated(true);
        setCart((current) => keepDeliveryExceptions(applyFulfillmentDefault(data.cart), current));
        settleFulfillmentSaveOnCart();
    };

    const notify = (data?: IErrorDTO) => {
        // "Customer selects date": the chosen date is no longer selectable (any choke point: the date save, the
        // payment intent, Place Order). The picker shows the new earliest date and the refreshed cart (its
        // rules carry it) re-defaults the choice; the customer re-picks or places the order with the new date.
        if (data && isDeliveryDateNotSelectableNotifyMessage(data.message)) {
            setRejectedEarliestDate(data.earliestDeliveryDate || null);
        }

        // The pending delivery-day save failed: its own failure, paired by the echoed requestId (or, from an API
        // that predates the echo, any cart-prop failure). The cart keeps its previous date. The API may already
        // have released the attached Stripe intent before failing, so the cached intent is dropped and the cart
        // re-fetched, as on success, and the intent effect mints a fresh one.
        const pendingDeliveryDate = pendingDeliveryDateRef.current;
        if (
            pendingDeliveryDate &&
            (data?.requestId === pendingDeliveryDate.requestId ||
                (data?.requestId === undefined && data?.message === "ERROR_UPDATING_CART_PROP"))
        ) {
            settleDeliveryDateSave();
            setPlaceOrderActive(false);
            dropPaymentIntent();
            getCart();
            return;
        }

        // The pending fulfillment save failed, whatever the reason: paired by the echoed requestId (or, from an
        // API that predates the echo, a cart-prop failure on the net-terms path). Notifications about anything
        // else leave the save alone.
        const fulfillmentRequestId = fulfillmentRequestIdRef.current;
        const isFulfillmentSaveFailure =
            !!fulfillmentRequestId &&
            (data?.requestId === fulfillmentRequestId ||
                (!payWithStripe && data?.requestId === undefined && data?.message === "ERROR_UPDATING_CART_PROP"));

        if (isFulfillmentSaveFailure) {
            fulfillmentSaveAnswered();

            // Net terms: re-fetch the cart so the radio and totals show what the server kept; the updating state
            // clears when it lands. (Card: the handlers below show any reason they know, and the intent is dropped
            // and the cart re-fetched at the end either way.)
            if (!payWithStripe) {
                setPlaceOrderActive(false);
                getCart();
                return;
            }
        }

        // A createPaymentIntent failure, paired with its request by the echoed requestId.
        const intentRequest = data?.requestId ? intentRequestsRef.current.get(data.requestId) : undefined;

        if (data?.requestId && !isFulfillmentSaveFailure) {
            intentRequestsRef.current.delete(data.requestId);

            // Sent before the latest cart edit (or by an earlier visit to this page): the error is about a cart
            // that no longer exists, so it must not drop the live intent or mark the current cart as rejected.
            // A promo rejection did remove the code from the cart, so its reason is still shown.
            if (intentRequest?.generation !== intentGenerationRef.current) {
                if (PromoCodeService.isPromoCodeNotifyMessage(data.message)) {
                    setPromoErrorKey(data.message.toLowerCase());
                    getCart();
                }

                return;
            }
        }

        setPlaceOrderActive(false);
        // Any Notify while a promo apply / remove is in flight means it failed (the API's fallback
        // codes ERROR_APPLYING_PROMO_CODE / ERROR_REMOVING_PROMO_CODE lack the ERROR_PROMO_CODE_
        // prefix), so always release the busy state.
        setPromoApplying(false);

        // A carried promo code stopped validating at a checkout choke point (createPaymentIntent /
        // completeCheckout). The API already cleared it from the cart, so reload the cart, show the
        // reason inline, and drop the cached intent so the next cart change mints a fresh one against
        // the corrected total (see the createPaymentIntent effect below).
        if (data && PromoCodeService.isPromoCodeNotifyMessage(data.message)) {
            setPromoErrorKey(data.message.toLowerCase());
            dropPaymentIntent();
            getCart();
        }

        // Below the supplier's minimum order total (createPaymentIntent / updatePaymentIntent /
        // completeCheckout). The API already cancelled any attached intent; reload the cart so the notice
        // shows the current shortfall. A rejected create remembers the cart it was sent for, so the intent
        // effect doesn't retry that same cart (only a create can loop; update / complete don't re-fire it).
        if (data && MinimumOrderService.isMinOrderTotalNotifyMessage(data.message)) {
            if (intentRequest) {
                minOrderRejectedSignatureRef.current = intentRequest.signature;
            } else if (intentRequestSignatureRef.current) {
                minOrderRejectedSignatureRef.current = intentRequestSignatureRef.current;
                intentRequestSignatureRef.current = null;
            }

            dropPaymentIntent();
            getCart();
        }

        // Stripe refused to update the cached intent, most likely because it was cancelled out from under
        // this page (the cart was edited in another tab or the app, or a cart edit failed after the API had
        // already released the intent). Drop it and reload the cart so the intent effect mints a fresh one
        // rather than leaving the payment form stuck loading.
        if (payWithStripe && data?.message === "ERROR_UPDATING_CART_PAYMENT_INTENT") {
            dropPaymentIntent();
            getCart();
        }

        // A customer-selected delivery date rejected at the payment intent or Place Order choke point: no
        // intent was handed out, so reload the cart (fresh rules) and let the picker roll the date forward.
        if (data && isDeliveryDateNotSelectableNotifyMessage(data.message)) {
            dropPaymentIntent();
            getCart();
        }

        // A failed card fulfillment save that none of the handlers above recovered from (each drops the intent
        // for the reason it knows): drop the intent too, since the API may have cancelled it, and re-fetch the
        // cart, so the fulfillment choice and Place Order unlock whatever the error was.
        if (isFulfillmentSaveFailure && paymentIntentIdRef.current !== null) {
            dropPaymentIntent();
            getCart();
        }
    };

    const applyCartPromoCode = (code: string) => {
        const payload: ICartApplyPromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            cartGuid: cart?.guid,
            code,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.CartApplyPromoCode, payload);
    };

    const removeCartPromoCode = () => {
        const payload: ICartRemovePromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            cartGuid: cart?.guid,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.CartRemovePromoCode, payload);
    };

    /**
     * Apply / remove result. An invalid code is a normal result (`validation.valid === false`) that
     * leaves the cart untouched. On success the API has cancelled any attached Stripe intent, so the
     * cached intent is dropped and the cart effect creates a new one for the discounted amount.
     */
    const onCartPromoCodeResult = (data: ICartOnApplyPromoCodeDTO) => {
        setPromoApplying(false);

        if (data.validation && data.validation.valid === false) {
            setPromoErrorKey(PromoCodeService.getErrorMessageKey(data.validation.reason));
            return;
        }

        setPromoErrorKey(null);
        dropPaymentIntent();
        setCart((current) => keepDeliveryExceptions(applyFulfillmentDefault(data.cart), current));
        settleFulfillmentSaveOnCart();
    };

    const onGetCart = (data: IOnGetCartDTO) => {
        setCart(applyFulfillmentDefault(data.cart));
        settleFulfillmentSaveOnCart();
    };

    useEffect(() => {
        if (!initTotalsCalculated && cart) {
            const payload: ICartCalculateTotalsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                cartGuid: cart.guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartCalculateTotals, payload);
        }

        if (!setupInventoryOn && cart) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [cart]);

    useEffect(() => {
        if (cart && initTotalsCalculated) {
            const rejectedForMinOrder = minOrderRejectedSignatureRef.current === cartTotalsSignature(cart);

            if (!paymentIntent && cart?.Cart_Items?.length && !isBelowMinOrder && !rejectedForMinOrder) {
                createPaymentIntent();
            }
        }
    }, [cart, initTotalsCalculated]);

    const onUpdateShipTo = (data: IOnUpdateCartShipToDTO) => {
        dropPaymentIntent();
        getCart();
    };

    const onUpdateItem = () => {
        setUpdatingItemGuid("");
        dropPaymentIntent();
        getCart();
    };

    const onDeleteItem = (data: IOnDeleteCartItemDTO) => {
        dropPaymentIntent();
        getCart();
    };

    const onUpdateProp = (data: IOnUpdateCartPropDTO) => {
        // The pending delivery-day save is settled once the server's cart carries that date, whichever
        // cart-prop request this response belongs to: a cart reflecting the new date proves the write landed.
        if (pendingDeliveryDateRef.current && toDeliveryDateKey(data.cart?.delivery_date) === pendingDeliveryDateRef.current.date) {
            settleDeliveryDateSave();
        }

        const notification: INotification = {
            message: "successfully_updated_cart",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        // Reflect the saved delivery-date choice (and its deadline context) right away so the picker
        // does not sit on the previous date until the refresh lands. Only these fields are taken from
        // the update payload: its cart items are not run through the signed-URL pass that getCart does.
        if (data.cart) {
            setCart((current) =>
                current
                    ? {
                          ...current,
                          delivery_date: data.cart.delivery_date,
                          deliveryBlackoutDates: data.cart.deliveryBlackoutDates ?? current.deliveryBlackoutDates,
                          deliveryScheduleOverrides: data.cart.deliveryScheduleOverrides ?? current.deliveryScheduleOverrides,
                          deliveryDateRules: data.cart.deliveryDateRules ?? current.deliveryDateRules,
                      }
                    : current
            );
        }

        // The pending net-terms fulfillment save is answered only by its own response (see isFulfillmentSaveAnswer);
        // the re-fetched cart below then settles it.
        if (isFulfillmentSaveAnswer(data.requestId)) {
            fulfillmentSaveAnswered();
        }
        dropPaymentIntent();
        getCart();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CartOnCreatePaymentIntent, fn: onCreatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnUpdatePaymentIntent, fn: onUpdatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnUpdateShipTo, fn: onUpdateShipTo },
        { endpoint: EndpointsEnum.Notify, fn: notify },
        { endpoint: EndpointsEnum.CartOnDeleteItem, fn: onDeleteItem },
        { endpoint: EndpointsEnum.CartOnUpdateItem, fn: onUpdateItem },
        { endpoint: EndpointsEnum.CartOnCalculateTotals, fn: onCalculateTotals },
        { endpoint: EndpointsEnum.CartOnUpdateProp, fn: onUpdateProp },
        { endpoint: EndpointsEnum.CartOnApplyPromoCode, fn: onCartPromoCodeResult },
        { endpoint: EndpointsEnum.CartOnRemovePromoCode, fn: onCartPromoCodeResult },
    ];

    useEffect(() => {
        if (!stripeLoaded) {
            if (payWithStripe && supplierModel.Supplier_Integration.stripe_publishable_api_key) {
                loadStripe(supplierModel.Supplier_Integration.stripe_publishable_api_key).then((_stripe) => {
                    setStripePromise(_stripe);
                });
            }

            setStripeLoaded(true);
        }

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getCart();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }

            const listeners = socket.listeners(EndpointsEnum.CartOnValidateInventory);

            // to be sure we clean up all the listeners that are CartOnValidateInventory and CartOnCompleteCheckout
            // we get into a weird state sometimes, no idea why yet.
            socket.removeAllListeners(EndpointsEnum.CartOnValidateInventory);
            socket.removeAllListeners(EndpointsEnum.CartOnCompleteCheckout);

            // Leaving checkout abandons any cart fetch still in flight or waiting to retry, so neither lands
            // (or reports a failure) on another page.
            if (getCartRetryTimerRef.current) {
                clearTimeout(getCartRetryTimerRef.current);
                getCartRetryTimerRef.current = null;
            }

            getCartRequestRef.current++;
        };
    }, []);

    useEffect(() => {
        if (!setupInventoryOn && stripe) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [stripe]);

    // Cart edits each re-fetch the cart, so fetches can overlap. Only the latest one's answer is applied, or
    // an older cart arriving last would overwrite the newer one. A failed latest fetch is retried rather than
    // falling back to an older answer, whose totals the API may no longer charge; once the retries run out
    // the customer is told, since a dropped intent is only replaced once a cart lands.
    const getCartRequestRef = useRef(0);
    const getCartRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const getCart = async (attempt = 0) => {
        const request = ++getCartRequestRef.current;

        try {
            const response = await CartService.getCart(userModel, supplierModel, vendorModel);

            if (request === getCartRequestRef.current) {
                onGetCart(response);
            }
        } catch (error) {
            console.error("Failed to get cart:", error);

            if (request !== getCartRequestRef.current) {
                return;
            }

            if (attempt < GET_CART_RETRY_DELAYS_MS.length) {
                getCartRetryTimerRef.current = setTimeout(() => {
                    getCartRetryTimerRef.current = null;

                    // A fetch started meanwhile supersedes this retry.
                    if (request === getCartRequestRef.current) {
                        getCart(attempt + 1);
                    }
                }, GET_CART_RETRY_DELAYS_MS[attempt]);

                return;
            }

            // No refreshed cart is coming, so do not leave the fulfillment choice and Place Order locked.
            settleFulfillmentSave();

            const notification: INotification = {
                message: "error_getting_cart",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.error,
            };

            uiContext.notifyFn(null, notification);
        }
    };

    const validateInventory = () => {
        refreshOnValidateInventory();

        const payload: ICartValidateInventoryDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
        };

        socket.emit(EndpointsEnum.CartValidateInventory, payload);

        // console.log("validateInventory");

        setPlaceOrderActive(true);
    };

    const onValidateInventory = (data: ICartOnValidateInventoryDTO) => {
        // console.log("onValidateInventory");
        // Unavailable items block both paths: the API does not re-check inventory when it creates the order.
        if (data.cartItems?.length) {
            setPlaceOrderActive(false);
            return;
        }

        if (!payWithStripe) {
            // console.log("onValidateInventory no stripe");
            refreshOnCompleteCheckout();

            const payload: ICartCompleteCheckoutDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            if (cart?.note) {
                payload.note = cart.note;
            }

            if (cart?.po_no) {
                payload.po_no = cart.po_no;
            }

            Object.assign(payload, shippingInstructionsPayload(cart));

            if (cart?.qbd_ship_via?.ListID) {
                payload.qbd_ship_via = cart.qbd_ship_via;
            }

            Object.assign(payload, fulfillmentPayload(cart));

            const utm = getCapturedUtm();
            if (utm) {
                payload.utm = utm;
            }

            socket.emit(EndpointsEnum.CartCompleteCheckout, payload);
        } else {
            // console.log("onValidateInventory else");
            // process the order as normal
            // form={"paymentForm"} type={"submit"} // how to manually submit the form data?
            // @ts-expect-error
            const paymentFormElem = document.forms.paymentForm;

            if (paymentFormElem) {
                handleSubmit();
            }
        }
    };

    const onCompleteCheckout = (data: ICartOnCompleteCheckoutDTO) => {
        // console.log("onCompleteCheckout");
        if (cart) {
            const url = `/ecomm/checkout/complete?g=${cart.guid}`;

            history(url);
        }
    };

    const pollForInactiveStatusAndRedirect = async (cartGuid: string) => {
        const POLL_MAX_ATTEMPTS = 6;
        const POLL_INTERVAL_MS = 1000;
        const inactiveStatuses: CartStatusEnum[] = [CartStatusEnum.FAILED, CartStatusEnum.PAYMENT_ERRORED, CartStatusEnum.PAYMENT_PENDING];

        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
            try {
                const statusResp = await CartService.getCartStatus(
                    cartGuid,
                    userModel,
                    supplierModel,
                    userModel.isSupplierUser ? undefined : vendorModel
                );

                if (inactiveStatuses.includes(statusResp.status as CartStatusEnum)) {
                    refreshInactiveCarts();
                    history(`/ecomm/inactive-cart/${cartGuid}`);
                    return;
                }
            } catch (err) {
                console.warn("Failed to refresh cart status after Stripe decline", err);
            }

            if (attempt < POLL_MAX_ATTEMPTS - 1) {
                await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            }
        }
    };

    const handleSubmit = async () => {
        // event.preventDefault();

        if (!stripe || !elements || !cart) {
            return;
        }

        // first before submitting we need to validate the inventory in the cart.

        const apiUrl = SocketService.getApiEndpointURl();

        let result;
        try {
            result = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${apiUrl}/stripe-complete-checkout/${cart.guid}`,
                },
                redirect: "if_required",
            });
        } catch (err) {
            console.warn("stripe.confirmPayment threw", err);
            setPlaceOrderActive(false);

            const message = err instanceof Error && err.message ? err.message : "pending_cart_detail_errors_retry";
            const isFormatted = err instanceof Error && !!err.message;
            const notification: INotification = {
                message,
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification, isFormatted);

            void pollForInactiveStatusAndRedirect(cart.guid);
            return;
        }

        if (result.error) {
            // Restore the button right away so the UI isn't stuck behind the
            // status poll. The poll runs in the background and redirects the
            // user to the recovery page if the backend flips the cart status
            // within the poll window.
            setPlaceOrderActive(false);

            const hasStripeMessage = !!result.error.message;
            const notification: INotification = {
                message: hasStripeMessage ? result.error.message! : "pending_cart_detail_errors_retry",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification, hasStripeMessage);

            // Stripe's webhook is what flips the cart to FAILED / PAYMENT_ERRORED
            // on the backend, and that arrives slightly after confirmPayment
            // resolves. Poll briefly so we can redirect to the inactive-cart
            // recovery page as soon as the status lands.
            const cartGuid = cart.guid;
            void pollForInactiveStatusAndRedirect(cartGuid);
            return;
        }

        // Success path with `redirect: 'if_required'`: Stripe didn't redirect
        // because no 3DS / wallet step was needed. Tell the backend to confirm
        // the payment intent and land the user on the complete page.
        const paymentIntent = "paymentIntent" in result ? result.paymentIntent : undefined;
        if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
            try {
                await CartService.confirmCartPayment(
                    cart.guid,
                    userModel,
                    supplierModel,
                    userModel.isSupplierUser ? undefined : vendorModel
                );
            } catch (err) {
                console.warn("Failed to confirm cart payment after successful Stripe intent", err);
            }
            history(`/ecomm/checkout/complete?g=${cart.guid}`);
            return;
        }

        // Anything else (unexpected status, no paymentIntent) — restore the
        // button so the user can retry.
        setPlaceOrderActive(false);
    };

    const setPaymentFormRef = (formRef: HTMLFormElement | null) => {
        setPaymentForm(formRef);
    };

    const setElementsFn = (_elements: StripeElements | null) => {
        setElements(_elements);
    };

    const setStripeFn = (_stripe: Stripe | null) => {
        setStripe(_stripe);
    };

    const updateAddlInput = (cartItem: ICart_Item, addlInput: string) => {
        const payload: IUpdateCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            addlInput,
            cartItemGuid: cartItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartUpdateItem, payload);

        setCartUpdating(true);
        setUpdatingItemGuid(cartItem.guid);
    };

    const updateQty = (cartItem: ICart_Item, qty: number) => {
        if (cartItem.qty !== qty) {
            const payload: IUpdateCartItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                qty,
                cartItemGuid: cartItem.guid,
                uomSetId: cartItem.uom_set_id,
                recalculateTotals: true,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartUpdateItem, payload);

            setCartUpdating(true);
            setUpdatingItemGuid(cartItem.guid);
        }
    };

    const deleteItem = (cartItem: ICart_Item) => {
        const payload: IDeleteCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartItemGuid: cartItem.guid,
            recalculateTotals: true,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartDeleteItem, payload);

        setCartUpdating(true);
    };

    const updateNote = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.note = e.target.value;

            setCart(updatedCart);
        }
    };

    const updatePo = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.po_no = e.target.value;

            setCart(updatedCart);
        }
    };

    const updateShippingInstructions = (e: ChangeEvent<any>) => {
        if (cart) {
            setCart({
                ...cart,
                shipping_instructions: e.target.value,
            });
        }
    };

    // Credit card: the order is created later by the Stripe webhook from the cart, with no client
    // payload, so an edit must reach the cart before the payment is confirmed. Persist it through the
    // payment intent on blur (like ship via); Place Order stays disabled until the intent answers.
    const persistShippingInstructions = () => {
        if (!showShippingInstructions || !payWithStripe || !paymentIntent || !cart) {
            return;
        }

        const shippingInstructions = resolveShippingInstructions(cart);

        if (shippingInstructions === sentShippingInstructionsRef.current) {
            return;
        }

        const payload: IUpdatePaymentIntentDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            payment_intent_id: paymentIntent.paymentIntentId,
            ...shippingInstructionsPayload(cart),
        };

        if (cart.qbd_ship_via?.ListID) {
            payload.qbd_ship_via = cart.qbd_ship_via;
        }

        socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

        setPaymentIntentLoading(true);
    };

    const updateShipVia = (selected: { value: string; title: string }) => {
        const option = (supplierModel.Supplier_Integration?.qbd_ShipMethodOptions || [])
            .filter((item) => item.IsActive)
            .find((item) => item.ListID === selected?.value);

        if (cart) {
            setCart({
                ...cart,
                qbd_ship_via: option || null,
            });
        }

        // Keep the credit-card payment intent in sync so the cart carries the selected ship method.
        if (payWithStripe && paymentIntent && option?.ListID) {
            const payload: IUpdatePaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                payment_intent_id: paymentIntent.paymentIntentId,
                qbd_ship_via: option,
                ...shippingInstructionsPayload(cart),
            };

            socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const updateFulfillment = (e: ChangeEvent<any>) => {
        const choice = e.target.value as EcommerceFulfillmentEnum;
        const flags = fulfillmentFlags(choice);

        if (!cart) {
            return;
        }

        const updatedCart: ICart = { ...cart, ...flags };

        setCart(updatedCart);

        if (payWithStripe) {
            // Credit card: persist through the payment intent (like ship via) so the intent amount is
            // refreshed with the recalculated shipping, then re-fetch the cart to show it. Without an
            // intent yet, the flags ride along on the intent create.
            if (paymentIntent) {
                const requestId = `fulfillment-${++fulfillmentRequestSeq}`;
                const payload: IUpdatePaymentIntentDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    vendorGuid: vendorModel.guid,
                    payment_intent_id: paymentIntent.paymentIntentId,
                    ...flags,
                    ...shippingInstructionsPayload(cart),
                    requestId,
                };

                if (cart.qbd_ship_via?.ListID) {
                    payload.qbd_ship_via = cart.qbd_ship_via;
                }

                refreshCartAfterIntentRef.current = true;
                startFulfillmentSave(requestId, flags);

                socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

                setPaymentIntentLoading(true);
            }
        } else {
            // Net terms: persist on the cart now so the server recalculates shipping and the summary
            // updates. po_no / note ride along so the re-fetched cart keeps what has been typed.
            const requestId = `fulfillment-${++fulfillmentRequestSeq}`;
            const payload: IUpdateCartPropDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                po_no: cart.po_no,
                note: cart.note,
                ...shippingInstructionsPayload(cart),
                ...flags,
                requestId,
            };

            startFulfillmentSave(requestId, flags);

            socket.emit(EndpointsEnum.CartUpdateProp, payload);
        }
    };

    const saveCartMetaData = async () => {
        if (cart) {
            const payload: IUpdateCartPropDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                po_no: cart.po_no,
                note: cart.note,
                ...shippingInstructionsPayload(cart),
                ...fulfillmentPayload(cart),
            };

            socket.emit(EndpointsEnum.CartUpdateProp, payload);
        }
    };

    // Delivery Deadlines: the customer picked one of the offered delivery dates. Persisted through the
    // cart (like fulfillment) so it carries onto the order on both the net-terms and credit-card paths;
    // the server validates it against the vendor's schedule and the refreshed cart drives the picker.
    const updateDeliveryDate = (deliveryDate: string) => {
        // One save at a time: the picker is read-only while a save is pending, so this only guards other callers.
        if (!cart || pendingDeliveryDateRef.current) {
            return;
        }

        const requestId = `delivery-date-${++deliveryDateRequestSeq}`;
        const payload: IUpdateCartPropDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            po_no: cart.po_no,
            note: cart.note,
            ...shippingInstructionsPayload(cart),
            ...fulfillmentPayload(cart),
            delivery_date: deliveryDate,
            requestId,
        };

        pendingDeliveryDateRef.current = { date: deliveryDate, requestId };
        setDeliveryDateSaving(true);
        setRejectedEarliestDate(null);
        socket.emit(EndpointsEnum.CartUpdateProp, payload);
    };

    const goBack = () => {
        history(-1);
    };

    // A summary amount a fulfillment change can move (tax, shipping, total): a skeleton bar stands in for it
    // while the change saves, since the amount on hand is the previous choice's.
    const renderUpdatingAmount = (amount: string) =>
        fulfillmentSaving ? <OwSkeleton width={"4.5rem"} testId={"checkout-summary-skeleton"} /> : amount;

    return (
        <ECommerceCheckoutContainerDiv className={"container-fluid container-md"}>
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                <OwButton fn={goBack} leftIconClass="fas fa-arrow-left" className="btn-link p-0 me-3" ariaLabel="Go back" title={"back"} />
            </div>
            <div className="d-flex align-items-center">
                <OwTitle title={"checkout"} className="mb-0" isBold={true} />
            </div>

            <ECommerceInsideContainer className={"d-flex flex-column flex-md-row flex-grow-1 flex-md-grow-0 row"}>
                <ECommerceMainDetailsDiv className={"col-md-8 col-lg-6 col-xl-6"}>
                    <ECommerceShippingAddressContainerDiv className={"flex-column"}>
                        {cart?.deliveryDateRules ? (
                            // "Customer selects date" (OR-1531): the account's cutoff / lead days / blocked days decide
                            // what can be picked; the vendor's schedule template (if any) does not apply.
                            <CustomerDeliveryDatePicker
                                rules={cart.deliveryDateRules}
                                selectedDate={cart.delivery_date}
                                onSelectDate={updateDeliveryDate}
                                isReadOnly={deliveryDateSaving}
                                rejectedEarliestDate={rejectedEarliestDate}
                            />
                        ) : (
                            <DeliveryDeadlineInfo
                                schedule={cart?.Vendor?.Delivery_Schedule}
                                timezone={cart?.Vendor?.timezone || supplierModel?.timezone}
                                blackoutDates={cart?.deliveryBlackoutDates}
                                overrides={cart?.deliveryScheduleOverrides}
                                selectedDate={cart?.delivery_date}
                                onSelectDate={updateDeliveryDate}
                                isReadOnly={deliveryDateSaving}
                            />
                        )}
                        <div className="">
                            <OwTitle
                                title={cart?.is_local_pickup ? "1_pickup_address" : "1_shipping_address"}
                                tag={TitleTagEnum.H5}
                                isUnderline={true}
                            />
                        </div>
                        <div className="w-100">
                            {cart && cart.is_local_pickup ? (
                                // Local Pickup: the order is collected at the supplier, so show where to pick
                                // it up instead of the customer ship-to form.
                                <AddressDisplay
                                    address={supplierModel.Ship_To}
                                    showName={true}
                                    showCompanyName={true}
                                    showPhone={true}
                                    hideFax={true}
                                    hideMobile={true}
                                    hideEmail={true}
                                />
                            ) : cart ? (
                                <EcommerceCheckoutShipTo
                                    cart={cart}
                                    socket={socket}
                                    notifyFn={uiContext.notifyFn}
                                    setActivityIndicator={uiContext.setActivityIndicator}
                                />
                            ) : null}
                        </div>
                    </ECommerceShippingAddressContainerDiv>

                    {payWithStripe ? (
                        <ECommercePaymentMethodContainerDiv>
                            <div className="">
                                <OwTitle title={"2_payment_method"} tag={TitleTagEnum.H5} isUnderline={true} />
                            </div>
                            <div className="w-100">
                                {paymentIntent && cart ? (
                                    // Keyed on the intent: Elements treats options.clientSecret as immutable, so a
                                    // different intent must re-mount the card form or it would still pay the old one.
                                    <Elements key={paymentIntent.paymentIntentId} stripe={stripePromise} options={paymentIntent as any}>
                                        <EcommerceStripeCardForm
                                            socket={socket}
                                            cart={cart}
                                            isCartValid={isCartValid}
                                            setPaymentFormRef={setPaymentFormRef}
                                            setElements={setElementsFn}
                                            setStripe={setStripeFn}
                                        />
                                    </Elements>
                                ) : (
                                    <div>
                                        <ActivityIndicator active={true} /> <OwLabel title={"loading"} />
                                    </div>
                                )}
                            </div>
                        </ECommercePaymentMethodContainerDiv>
                    ) : null}

                    <ECommerceReviewItemsContainerDiv className={"flex-column"}>
                        <div className="">
                            {payWithStripe ? (
                                <OwTitle title={"3_review_items"} tag={TitleTagEnum.H5} isUnderline={true} />
                            ) : (
                                <OwTitle title={"2_review_items"} tag={TitleTagEnum.H5} isUnderline={true} />
                            )}
                        </div>
                        <div className=" flex-grow-1 flex-column">
                            {cart?.Cart_Items?.filter((cartItem) => !cartItem.main_kit_cart_item_id).map((cartItem) => {
                                let kitBuilderItems: ICart_Item[] | undefined = [];

                                if (cartItem.is_kit_builder) {
                                    kitBuilderItems = cart?.Cart_Items?.filter((kitCartItem) => {
                                        return kitCartItem.main_kit_cart_item_id === cartItem.id;
                                    });
                                }

                                return (
                                    <EcommerceCheckoutCartItem
                                        key={cartItem.guid}
                                        cartItem={cartItem}
                                        priceGroup={vendorModel.Price_Group}
                                        updateQty={updateQty}
                                        deleteItem={deleteItem}
                                        isUpdating={cartItem.guid === updatingItemGuid}
                                        updateAddlInput={updateAddlInput}
                                        kitBuilderItems={kitBuilderItems}
                                        editKit={setEditingKitItem}
                                    />
                                );
                            })}
                            {editingKitItem && cart?.Cart_Items ? (
                                <EcommerceCartKitEditor
                                    socket={socket}
                                    cartItem={editingKitItem}
                                    cartItems={cart.Cart_Items}
                                    recalculateTotals={true}
                                    onSave={() => {
                                        setCartUpdating(true);
                                        setUpdatingItemGuid(editingKitItem.guid);
                                    }}
                                    onClose={() => setEditingKitItem(undefined)}
                                />
                            ) : null}
                        </div>
                    </ECommerceReviewItemsContainerDiv>
                </ECommerceMainDetailsDiv>
                <EcommerceRightSideContainer className={"col-md-4 col-lg-3 flex-grow-1 flex-md-grow-0 flex-column-reverse flex-sm-column"}>
                    <ECommerceFloatingSummaryDiv className={"mt-4 mt-md-0 mb-2 mb-md-0"}>
                        {!isAddressValid && (
                            <div className="alert alert-warning mb-2">
                                <div className="text-center">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                </div>
                                <div>
                                    <OwLabel title={"address_required_for_checkout"} />
                                </div>
                            </div>
                        )}
                        {isBelowMinOrder && minOrder ? (
                            <div className="alert alert-warning mb-2">
                                <div className="text-center">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                </div>
                                <div>
                                    <MinOrderTotalNotice
                                        amounts={{
                                            minimum: currencyFormatter.format(minOrder.minimum),
                                            shortfall: currencyFormatter.format(minOrder.shortfall),
                                        }}
                                    />
                                </div>
                            </div>
                        ) : null}
                        <div>
                            <OwButton
                                title={"place_your_order"}
                                className={"btn-warning"}
                                btnSizeClass="btn-lg"
                                fn={validateInventory}
                                active={placeOrderActive}
                                disableOnActive={true}
                                activeTitle={"please_wait"}
                                disabled={
                                    (!paymentIntent && payWithStripe) ||
                                    !cart?.Cart_Items?.length ||
                                    (!!supplierModel.orders_require_po && !cart?.po_no?.length) ||
                                    paymentIntentLoading ||
                                    deliveryDateSaving ||
                                    fulfillmentSaving ||
                                    (!!cart?.deliveryDateRules && !cart?.delivery_date) ||
                                    !isAddressValid ||
                                    isBelowMinOrder
                                }
                            />
                        </div>
                        {!hidePricing ? (
                            <div>
                                <hr />
                            </div>
                        ) : null}
                        {!hidePricing ? (
                            <div data-testid={"checkout-summary"} aria-busy={fulfillmentSaving}>
                                <div>
                                    <OwTitle title={"order_summary"} tag={TitleTagEnum.H5} />
                                </div>
                                <div className={"items"}>
                                    <OwLabel title={"items"} /> {currencyFormatter.format(cart?.sub_total || 0)}
                                </div>

                                {cart?.discount_total && cart?.discount_total > 0 ? (
                                    <div className={"discount"}>
                                        <OwLabel title={"discount"} /> -{currencyFormatter.format(cart?.discount_total || 0)}
                                    </div>
                                ) : null}

                                {supplierModel.has_promo_codes ? (
                                    <div className={"promo-code mt-2 mb-2"} data-testid={"checkout-promo-code"}>
                                        <OwPromoCodeInput
                                            appliedCode={cart?.promo_code}
                                            appliedDiscount={cart?.promo_discount_total}
                                            currency={currencyName}
                                            collapsible={true}
                                            rowLayout={true}
                                            onApply={applyCartPromoCode}
                                            onRemove={removeCartPromoCode}
                                            errorKey={promoErrorKey}
                                            applying={promoApplying}
                                            disabled={paymentIntentLoading || placeOrderActive || !cart?.Cart_Items?.length}
                                            pricingPrecision={2}
                                            isSmall={true}
                                        />
                                    </div>
                                ) : cart?.promo_code ? (
                                    <div className={"promo-code"}>
                                        <OwLabel title={"promo_code"} /> ({cart.promo_code}) -
                                        {currencyFormatter.format(cart.promo_discount_total || 0)}
                                    </div>
                                ) : null}

                                {vendorModel.tax_exempt ? null : (
                                    <div className={"tax"}>
                                        <OwLabel title={"tax"} /> {renderUpdatingAmount(currencyFormatter.format(cart?.tax || 0))}
                                    </div>
                                )}

                                <div className={"shipping"}>
                                    <OwLabel title={"shipping"} /> {renderUpdatingAmount(currencyFormatter.format(cart?.shipping || 0))}
                                </div>

                                <br />
                                <div className={"total"}>
                                    <OwLabel title={"order_total"} />{" "}
                                    {renderUpdatingAmount(currencyFormatter.format(cart?.grand_total || cart?.sub_total || 0))}
                                </div>
                            </div>
                        ) : null}
                    </ECommerceFloatingSummaryDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"po_no"} isBold={true} showIsReqAsterik={!!supplierModel.orders_require_po} />
                        </div>
                        <div>
                            <OwTextArea name={"po_no"} fn={updatePo} value={cart?.po_no || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"note"} isBold={true} />
                        </div>
                        <div>
                            <OwTextArea name={"note"} fn={updateNote} value={cart?.note || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    {showShippingInstructions ? (
                        <EcommerceNoteDiv data-testid={"checkout-shipping-instructions"}>
                            <div>
                                <OwLabel title={"shipping_instructions"} isBold={true} />
                            </div>
                            <div onBlur={persistShippingInstructions}>
                                <OwTextArea
                                    name={"shipping_instructions"}
                                    fn={updateShippingInstructions}
                                    value={resolveShippingInstructions(cart)}
                                />
                            </div>
                        </EcommerceNoteDiv>
                    ) : null}
                    {showFulfillmentChoice ? (
                        <EcommerceNoteDiv>
                            <div>
                                <OwLabel title={"fulfillment"} isBold={true} />
                            </div>
                            <div>
                                <OwRadioSelect
                                    name={"fulfillment"}
                                    supplierModel={supplierModel}
                                    options={fulfillmentOptions}
                                    value={fulfillmentChoice}
                                    fn={updateFulfillment}
                                    disabled={fulfillmentSaving}
                                />
                            </div>
                        </EcommerceNoteDiv>
                    ) : null}
                    {supplierModel.Supplier_Integration?.has_qbd && supplierModel.Supplier_Integration?.qbd_show_ship_via_options ? (
                        <EcommerceNoteDiv>
                            <div>
                                <OwLabel title={"ship_via"} isBold={true} />
                            </div>
                            <div>
                                <OwSelectOption
                                    fn={updateShipVia}
                                    name={"qbd_ship_via"}
                                    titleProp={"title"}
                                    valueProp={"value"}
                                    options={(supplierModel.Supplier_Integration?.qbd_ShipMethodOptions || [])
                                        .filter((item) => item.IsActive)
                                        .map((item) => {
                                            return {
                                                value: item.ListID,
                                                title: item.Name,
                                            };
                                        })}
                                    emitObject={true}
                                    selectedOption={cart?.qbd_ship_via}
                                    selectedOptionProp={"ListID"}
                                    showBlankFirst={true}
                                />
                            </div>
                        </EcommerceNoteDiv>
                    ) : null}
                    {payWithStripe ? (
                        <div className={"mt-2"}>
                            <OwButton title={"save"} fn={saveCartMetaData} />
                        </div>
                    ) : null}
                </EcommerceRightSideContainer>
            </ECommerceInsideContainer>
        </ECommerceCheckoutContainerDiv>
    );
};

const ECommerceCheckoutContainerDiv = styled.div``;

const ECommerceInsideContainer = styled.div`
    display: flex;
`;

const ECommerceShippingAddressContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommercePaymentMethodContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommerceReviewItemsContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const EcommerceRightSideContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ECommerceFloatingSummaryDiv = styled.div`
    //min-width: 13rem;
    border: 1px solid var(--main-text-color);
    border-radius: 3px;
    padding: 0.5rem;
    height: fit-content;

    button {
        width: 100%;
    }

    hr {
        color: var(--main-text-color);
        border-color: var(--main-text-color);
    }

    .items,
    .discount,
    .tax,
    .shipping,
    .total {
        display: flex;
        justify-content: space-between;
    }
`;

const ECommerceMainDetailsDiv = styled.div``;

const EcommerceNoteDiv = styled.div`
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    height: auto;
`;
